import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Suspense } from 'react';
import ErrorBoundary from '@/components/ErrorBoundary';
import { AuthProvider } from '@/contexts/AuthContext';
import { ImpersonateProvider } from '@/contexts/ImpersonateContext';
import Screensaver from '@/components/Screensaver';
import CookieBanner from '@/components/CookieBanner';
import GuidedTour from '@/components/tour/GuidedTour';
import { GuidedUpdateBanner } from '@/components/update/GuidedUpdateBanner';

import { LoadingSpinner } from '@/app/LoadingSpinner';
import { OfflineSyncBootstrap, PwaUpdatePrompt } from '@/app/bootstrap';
import { NotFound } from '@/app/lazyPages';
import { publicRoutes } from '@/app/routes/publicRoutes';
import { coreRoutes } from '@/app/routes/coreRoutes';
import { commerceRoutes } from '@/app/routes/commerceRoutes';
import { fiscalMiscRoutes } from '@/app/routes/fiscalMiscRoutes';
import { foodChrRoutes } from '@/app/routes/foodChrRoutes';
import { configCatalogRoutes } from '@/app/routes/configCatalogRoutes';
import { configSystemRoutes } from '@/app/routes/configSystemRoutes';

const queryClient = new QueryClient();

// Les groupes de routes sont composés comme fragments (appel de fonction, pas
// composant) car <Routes> n'accepte que <Route> et <React.Fragment> en enfants.
// L'ordre source d'origine est strictement préservé (strangler 1b).
const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <PwaUpdatePrompt />
        <OfflineSyncBootstrap />
        <Screensaver idleTimeout={600000} />
        <CookieBanner />
        <GuidedUpdateBanner />
        <AuthProvider>
          <ImpersonateProvider>
            <BrowserRouter>
              <Routes>
                {publicRoutes()}
                {coreRoutes()}
                {commerceRoutes()}
                {fiscalMiscRoutes()}
                {foodChrRoutes()}
                {configCatalogRoutes()}
                {configSystemRoutes()}
                <Route
                  path="*"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <NotFound />
                    </Suspense>
                  }
                />
              </Routes>
              <GuidedTour />
            </BrowserRouter>
          </ImpersonateProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
